import {
  BugIcon,
  CircleCheckIcon,
  CloudCogIcon,
  FileBoxIcon,
  FileQuestionIcon,
  HardHatIcon,
  HeartIcon,
  ListTodoIcon,
  NotepadTextDashedIcon,
  PuzzleIcon,
  SearchIcon,
  Send,
  SparklesIcon,
  Table2Icon,
} from 'lucide-react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'

import { NavMain } from '~/components/nav-main'
import { NavUser } from '~/components/nav-user'
import { ProtectedComponent } from '~/components/ProtectedComponent'
import { Sidebar, SidebarContent, SidebarFooter } from '~/components/ui/sidebar'
import { useIsFeatureEnabled } from '~/data/useData'
import {
  useIsZehAnalyticsV2Enabled,
  useIsZehEnabled,
  useIsZehGenieEnabled,
} from '~/modules/zeh/api/useData'
import { NavLanguage } from './nav-language'
import { AtSignIcon } from './ui/at-sign'
import { ChartLineIcon } from './ui/chart-line'
import { FileCheck2Icon } from './ui/file-check-2'
import { UsersIcon } from './ui/users'

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { t } = useTranslation()

  const { data: isZehEnabledResult } = useIsZehEnabled()
  const { data: isZehAnalyticsV2Enabled } = useIsZehAnalyticsV2Enabled()
  const { data: isZehGenieEnabled } = useIsZehGenieEnabled()
  const { data: isSearchRecordsEnabled } = useIsFeatureEnabled({ feature_flag: 'search_records' })
  const zehAnalyticsUrl = isZehAnalyticsV2Enabled === true ? '/zeh' : '/zeh/analytics'
  const zehChatUrl = isZehGenieEnabled === true ? '/zeh/report' : '/zeh/chat'

  const navigationSections = [
    {
      id: 'main',
      title: t('Your workspace'),
      type: 'main' as const,
      items: [
        {
          title: t('Zeh Analytics'),
          url: zehAnalyticsUrl,
          icon: ChartLineIcon,
          requiredPermission: 'create_conversation',
        },
        {
          title: t('Audits'),
          url: '/audit',
          icon: FileCheck2Icon,
          requiredPermission: 'list_inspection',
        },
        {
          title: t('Search'),
          url: '/search',
          icon: SearchIcon,
          hidden: isSearchRecordsEnabled === false,
        },
        {
          title: t('Chat'),
          url: zehChatUrl,
          icon: SparklesIcon,
          hidden: isZehEnabledResult?.result === false,
        },
      ],
    },
    {
      id: 'settings',
      title: t('Settings'),
      type: 'main' as const,
      requiredPermission: {
        operation: 'or' as const,
        permissions: ['list_user', 'list_company', 'list_asset'],
      },
      items: [
        {
          title: t('User'),
          url: '/users',
          icon: UsersIcon,
          requiredPermission: 'list_user',
        },
        {
          title: t('Company Settings'),
          url: '/companies',
          icon: AtSignIcon,
          requiredPermission: 'list_company',
        },
        {
          title: t('Assets Config'),
          url: '/assets_config',
          icon: CloudCogIcon,
          requiredPermission: 'list_asset',
        },
      ],
    },
    {
      id: 'admin',
      title: t('Admin'),
      type: 'main' as const,
      requiredPermission: {
        operation: 'or' as const,
        permissions: [
          'list_report_template',
          'list_inspection_checklist',
          'list_inspection_item',
          'list_component',
          'list_file_class',
          'list_component_parameter',
          'list_physical_item',
        ],
      },
      items: [
        {
          title: t('Report Templates'),
          url: '/report_templates',
          icon: NotepadTextDashedIcon,
          requiredPermission: 'list_report_template',
        },
        {
          title: t('Checklists'),
          url: '/inspection_checklists',
          icon: ListTodoIcon,
          requiredPermission: 'list_inspection_checklist',
        },
        {
          title: t('Tables'),
          url: '/tables',
          icon: Table2Icon,
          requiredPermission: 'list_inspection_checklist',
        },
        {
          title: t('Audit Items'),
          url: '/inspection_items',
          icon: CircleCheckIcon,
          requiredPermission: 'list_inspection_item',
        },
        {
          title: t('Asset Components'),
          url: '/components',
          icon: PuzzleIcon,
          requiredPermission: 'list_component',
        },
        {
          title: t('File Classes'),
          url: '/file_classes',
          icon: FileBoxIcon,
          requiredPermission: 'list_file_class',
        },
        {
          title: t('Compts. Parameters'),
          url: '/component_parameters',
          icon: FileQuestionIcon,
          requiredPermission: 'list_component_parameter',
        },
        {
          title: t('Physical Ver. Items'),
          url: '/physical_items',
          icon: HardHatIcon,
          requiredPermission: 'list_physical_item',
        },
      ],
    },
    {
      id: 'secondary',
      title: '',
      type: 'secondary' as const,
      className: 'mt-auto',
      items: [
        {
          title: t('Product Updates'),
          url: 'https://eyeson.userjot.com/updates',
          icon: HeartIcon,
          external: true,
        },
        {
          title: t('Report a bug'),
          url: 'https://eyeson.userjot.com/board/bugs?openFeedback',
          icon: BugIcon,
          external: true,
        },
        {
          title: t('Request a feature'),
          url: 'https://eyeson.userjot.com/board/features/?openFeedback',
          icon: Send,
          external: true,
        },
      ],
    },
  ]
  return (
    <Sidebar variant='inset' {...props} className='border-r border-r-gray-200'>
      <div className='flex items-start justify-start p-4'>
        <img
          src='/colored-logo-vertical.webp'
          alt='logo'
          className='rounded-sm object-contain h-full max-h-16'
        />
      </div>
      <SidebarContent>
        {navigationSections.map((section) => {
          const content = (
            <NavMain
              title={section.title}
              items={section.items}
              type={section.type}
              className={section.className}
            />
          )

          if (section.requiredPermission) {
            return (
              <ProtectedComponent key={section.id} requiredPermission={section.requiredPermission}>
                {content}
              </ProtectedComponent>
            )
          }

          return <React.Fragment key={section.id}>{content}</React.Fragment>
        })}
      </SidebarContent>
      <SidebarFooter>
        <NavLanguage />
        <NavUser />
      </SidebarFooter>
    </Sidebar>
  )
}
